'use client';

import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from 'react';
import WORDING from '@/wording';
import { MultiServicePreset } from '@/types';
import {
  Config,
  LanguageOptions,
  ServiceType,
  Wording,
  WordingMultiAstrologyMeditationLove,
  WordingMultiFitnessYogaPilates,
} from '@/types';

export type WordingType = Wording &
  Partial<WordingMultiFitnessYogaPilates> &
  Partial<WordingMultiAstrologyMeditationLove>;

// Define the correct type for WORDING
export type WordingMap = {
  [key in ServiceType]: Partial<Record<LanguageOptions, Wording>>;
} & {
  multiFitnessYogaPilates: Partial<
    Record<LanguageOptions, WordingMultiFitnessYogaPilates>
  >;
  multiAstrologyMeditationLove: Partial<
    Record<LanguageOptions, WordingMultiAstrologyMeditationLove>
  >;
};

// Context types
interface ConfigContextProps {
  config: Config; // Current configuration for the app
  setConfig: (config: Config) => void; // Function to update the config
  wording: WordingType; // Translations for the selected language
  updateWording: (serviceType: ServiceType, lang: LanguageOptions) => void; // Function to change the wording dynamically
  selectedLanguage: LanguageOptions; // Current langue for the app
  setSelectedLanguage: (language: LanguageOptions) => void; // Function to set a language for the app
  setLanguageAndWording: (
    serviceType: ServiceType,
    language: LanguageOptions
  ) => void; // Function to save selected language and change wording files used accordingly
}

// Create the context
const ConfigContext = createContext<ConfigContextProps | undefined>(undefined);

// Provider component
export const ConfigProvider = ({
  children,
  initialConfig,
  initialLanguage,
}: {
  children: ReactNode;
  initialConfig: Config;
  initialLanguage: LanguageOptions;
}) => {
  // State to store the current configuration
  const [config, setConfig] = useState<Config>(initialConfig);
  // State to store the current selected language, by default it's the first from the config
  const [selectedLanguage, setSelectedLanguage] =
    useState<LanguageOptions>(initialLanguage);

  const getMergedWording = (
    currentConfig: Config,
    serviceType: ServiceType,
    language: LanguageOptions
  ): WordingType | null => {
    const base = WORDING[serviceType]?.[language];
    if (!base) {
      return null;
    }

    if (currentConfig.multiService === MultiServicePreset.FYP) {
      const multi = WORDING.multiFitnessYogaPilates?.[language] ?? {};
      return {
        ...base,
        ...multi,
      };
    }

    if (currentConfig.multiService === MultiServicePreset.AML) {
      const multi = WORDING.multiAstrologyMeditationLove?.[language] ?? {};
      return {
        ...base,
        ...multi,
      };
    }

    return base as WordingType;
  };

  // Function to update both selectedLanguage and wording
  const setLanguageAndWording = (
    serviceType: ServiceType,
    language: LanguageOptions
  ) => {
    setSelectedLanguage(language);
    updateWording(serviceType, language);
  };

  // State to store the current wording based on the initial config
  const initialWording =
    getMergedWording(
      initialConfig,
      initialConfig?.serviceType,
      (initialLanguage ?? initialConfig?.languages[0]) as LanguageOptions
    ) ?? ({} as WordingType);

  const [wording, setWording] = useState<WordingType>(initialWording);

  // Function to update the wording dynamically
  const updateWording = (serviceType: ServiceType, lang: LanguageOptions) => {
    const nextWording = getMergedWording(config, serviceType, lang);
    if (!nextWording) {
      console.error(
        `[ConfigContext] Wording not found for ${serviceType} and ${lang}`
      );
      return;
    }

    setWording(nextWording);
  };

  useEffect(() => {
    const nextWording = getMergedWording(
      config,
      config.serviceType,
      selectedLanguage
    );

    if (nextWording) {
      setWording(nextWording);
    }
  }, [
    config,
    selectedLanguage,
  ]);

  // Values passed to the context
  const value: ConfigContextProps = {
    config,
    setConfig,
    wording,
    updateWording,
    selectedLanguage,
    setSelectedLanguage,
    setLanguageAndWording,
  };

  return (
    <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>
  );
};

// Hook to use the Config context and get the config
export const useConfig = (): ConfigContextProps => {
  const context = useContext(ConfigContext);

  if (!context) {
    console.warn("⚠️ useConfig() a été appelé en dehors d'un ConfigProvider.");
    return {
      config: {} as Config,
      setConfig: () => {},
      wording: {} as WordingType,
      updateWording: () => {},
      selectedLanguage: '' as LanguageOptions,
      setSelectedLanguage: () => {},
      setLanguageAndWording: () => {},
    };
  }

  return context;
};
